import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  CalendarDays, Clock, Users, ShoppingCart, Check,
  Loader2, Share2, AlertTriangle, Heart, User as UserIcon, X,
  ThumbsUp, HelpCircle, ChefHat, CheckCircle, Bookmark, Star, ArrowLeft,
  Wallet, Plus, CreditCard, Banknote, ArrowUpDown, Edit3, EyeOff, Eye
} from "lucide-react";
import gustoAvatarWhite from "@/assets/gusto-avatar-white.png";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { motion } from "framer-motion";
import ChefAvatar from "@/components/ChefAvatar";
import { GustoIcon } from "@/components/GustoLogo";

const RSVP_OPTIONS = [
  { value: "accepted", label: "Je serai là", icon: ThumbsUp, color: "bg-green-500 hover:bg-green-600 text-white" },
  { value: "maybe", label: "Peut-être", icon: HelpCircle, color: "bg-amber-500 hover:bg-amber-600 text-white" },
  { value: "declined", label: "Indispo", icon: X, color: "bg-red-500 hover:bg-red-600 text-white" },
];

const RSVP_BADGE = {
  accepted: { label: "Présent(e)", cls: "bg-green-100 text-green-700" },
  maybe: { label: "Peut-être", cls: "bg-amber-100 text-amber-700" },
  declined: { label: "Indispo", cls: "bg-red-100 text-red-700" },
};

const RATING_CRITERIA = [
  { key: "presentation", label: "Présentation", icon: "🎨" },
  { key: "saveurs", label: "Saveurs", icon: "👅" },
  { key: "originalite", label: "Originalité", icon: "💡" },
  { key: "satiete", label: "Satiété", icon: "🍽" },
];

const MealDetailPage = () => {
  const { mealId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [meal, setMeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestName, setGuestName] = useState(user?.pseudo || "");
  const [claiming, setClaiming] = useState(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [favoriting, setFavoriting] = useState(false);
  const [rating, setRating] = useState({ presentation: 0, saveurs: 0, originalite: 0, satiete: 0 });
  const [hoverRating, setHoverRating] = useState({});
  const [ratingComment, setRatingComment] = useState("");
  const [submittingRating, setSubmittingRating] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  // Budget & contribution
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [showContribute, setShowContribute] = useState(false);
  const [contribAmount, setContribAmount] = useState("");
  const [contribMethod, setContribMethod] = useState("cash");
  const [submittingContrib, setSubmittingContrib] = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ cost_per_person: "", estimated_budget: "", contribution_per_guest: "", contribution_enabled: false, budget_hidden: false });
  const [savingBudget, setSavingBudget] = useState(false);
  // Add items
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");

  useEffect(() => {
    fetchMeal();
  }, [mealId]);

  useEffect(() => {
    if (token && meal) fetchBudget();
  }, [meal?.id]);

  const fetchBudget = async () => {
    try {
      const res = await axios.get(`${API}/meals/${mealId}/budget-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBudgetSummary(res.data);
    } catch {}
  };

  const fetchMeal = async () => {
    try {
      const res = await axios.get(`${API}/meals/${mealId}`);
      setMeal(res.data);
      // Check if user already rated
      if (user && res.data.ratings) {
        const myRating = res.data.ratings.find((r) => r.user_id === user.id);
        if (myRating) {
          setHasRated(true);
          setRating({ presentation: myRating.presentation, saveurs: myRating.saveurs, originalite: myRating.originalite, satiete: myRating.satiete });
        }
      }
    } catch {
      toast.error("Repas introuvable");
    } finally {
      setLoading(false);
    }
  };

  const handleRsvp = async (status) => {
    if (!token) {
      toast.error("Connectez-vous pour répondre");
      return;
    }
    setRsvpLoading(true);
    try {
      await axios.post(`${API}/meals/${mealId}/rsvp`, { status }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchMeal();
      const labels = { accepted: "Vous serez là !", maybe: "Réponse enregistrée", declined: "Dommage, une prochaine fois !" };
      toast.success(labels[status]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleFinishMeal = async () => {
    setFinishing(true);
    try {
      const res = await axios.post(`${API}/meals/${mealId}/finish`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      await fetchMeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setFinishing(false);
    }
  };

  const handleFavoriteRecipe = async () => {
    if (!token) { toast.error("Connectez-vous"); return; }
    setFavoriting(true);
    try {
      const res = await axios.post(`${API}/meals/${mealId}/favorite-recipe`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setFavoriting(false);
    }
  };

  const handleSubmitRating = async () => {
    if (!token) { toast.error("Connectez-vous"); return; }
    if (Object.values(rating).some((v) => v === 0)) { toast.error("Notez tous les critères"); return; }
    setSubmittingRating(true);
    try {
      await axios.post(`${API}/meals/${mealId}/rate`, {
        ...rating,
        comment: ratingComment.trim() || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const total = Object.values(rating).reduce((a, b) => a + b, 0);
      toast.success(`Merci ! ${total}/20 — +3 points`);
      setHasRated(true);
      await fetchMeal();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmittingRating(false);
    }
  };


  const claimItem = async (itemId) => {
    if (!guestName.trim()) { toast.error("Indiquez votre prénom"); return; }
    setClaiming(itemId);
    try {
      await axios.post(`${API}/meals/${mealId}/claim/${itemId}?name=${encodeURIComponent(guestName.trim())}`);
      await fetchMeal();
      toast.success("C'est noté !");
    } catch { toast.error("Erreur"); } finally { setClaiming(null); }
  };

  const unclaimItem = async (itemId) => {
    setClaiming(itemId);
    try {
      await axios.post(`${API}/meals/${mealId}/unclaim/${itemId}`);
      await fetchMeal();
    } catch { toast.error("Erreur"); } finally { setClaiming(null); }
  };

  const shareLink = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: meal.title,
          text: `${meal.host_name} t'invite au repas "${meal.title}" ! Viens cocher ce que tu ramènes :`,
          url,
        });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié !");
    }
  };

  const handleContribute = async () => {
    if (!contribAmount || parseFloat(contribAmount) <= 0) { toast.error("Montant invalide"); return; }
    setSubmittingContrib(true);
    try {
      await axios.post(`${API}/meals/${mealId}/contribute`,
        { amount: parseFloat(contribAmount), method: contribMethod, paid: true },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      toast.success("Participation enregistree !");
      setContribAmount(""); setShowContribute(false);
      fetchMeal(); fetchBudget();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmittingContrib(false); }
  };

  const handleSaveBudget = async () => {
    setSavingBudget(true);
    try {
      const guestCount = (meal.invited_friend_ids || []).length + 1;
      const costPP = budgetForm.cost_per_person ? parseFloat(budgetForm.cost_per_person) : null;
      await axios.put(`${API}/meals/${mealId}/budget`, {
        cost_per_person: costPP,
        estimated_budget: costPP ? costPP * guestCount : (budgetForm.estimated_budget ? parseFloat(budgetForm.estimated_budget) : null),
        contribution_per_guest: budgetForm.contribution_per_guest ? parseFloat(budgetForm.contribution_per_guest) : null,
        contribution_enabled: budgetForm.contribution_enabled,
        budget_hidden: budgetForm.budget_hidden,
      }, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      toast.success("Budget mis a jour !");
      setEditingBudget(false);
      fetchMeal(); fetchBudget();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSavingBudget(false); }
  };

  const handleAddItem = async () => {
    if (!newItemName.trim()) return;
    try {
      await axios.post(`${API}/meals/${mealId}/add-items`,
        [{ name: newItemName.trim() }],
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      toast.success("Article ajoute !");
      setNewItemName(""); fetchMeal();
    } catch { toast.error("Erreur"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!meal) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Repas introuvable</p>
      </div>
    );
  }

  const claimedCount = meal.shopping_list?.filter((i) => i.claimed_by).length || 0;
  const totalItems = meal.shopping_list?.length || 0;
  const rsvps = meal.rsvps || {};
  const myRsvp = user ? rsvps[user.id]?.status : null;
  const isHost = user?.id === meal.host_id;

  const allAllergies = [];
  (meal.participants || []).forEach((p) => {
    const dp = p.dietary_profile || {};
    (dp.allergies || []).forEach((a) => {
      if (!allAllergies.find((x) => x.item === a)) {
        allAllergies.push({ name: p.pseudo, item: a });
      }
    });
  });

  // RSVP counts
  const rsvpCounts = { accepted: 0, maybe: 0, declined: 0 };
  Object.values(rsvps).forEach((r) => { if (rsvpCounts[r.status] !== undefined) rsvpCounts[r.status]++; });

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="meal-detail-page">
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-charcoal transition-colors"
          data-testid="meal-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Retour</span>
        </button>

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-charcoal to-gray-800 flex items-center justify-center shadow-lg">
            <img src={gustoAvatarWhite} alt="Gusto" className="w-9 h-9" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-charcoal">{meal.title}</h1>
          <p className="text-muted-foreground">
            Organisé par <strong>{meal.host_name}</strong>
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <CalendarDays className="w-4 h-4" />
              {new Date(meal.meal_date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            </span>
            {meal.meal_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" /> {meal.meal_time}
              </span>
            )}
          </div>
          {meal.message && (
            <p className="text-sm italic text-muted-foreground bg-gray-50 rounded-xl p-3 max-w-md mx-auto">
              "{meal.message}"
            </p>
          )}
        </div>

        {/* RSVP Section */}
        {user && !isHost && (
          <Card className="border-primary/20" data-testid="rsvp-section">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-charcoal mb-3 text-center">
                {myRsvp ? "Modifier votre réponse" : "Serez-vous présent(e) ?"}
              </p>
              <div className="flex gap-2 justify-center">
                {RSVP_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const isSelected = myRsvp === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      onClick={() => handleRsvp(opt.value)}
                      disabled={rsvpLoading}
                      className={`rounded-full gap-1.5 px-4 transition-all ${
                        isSelected
                          ? opt.color + " ring-2 ring-offset-2 ring-current scale-105"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid={`rsvp-${opt.value}`}
                    >
                      {rsvpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                      {opt.label}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* RSVP Summary for host */}
        {isHost && Object.keys(rsvps).length > 0 && (
          <Card data-testid="rsvp-summary">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-charcoal mb-3 flex items-center gap-2">
                <ChefHat className="w-4 h-4" /> Réponses des invités
              </p>
              <div className="flex gap-3 mb-3">
                <Badge className="bg-green-100 text-green-700">{rsvpCounts.accepted} présent(s)</Badge>
                <Badge className="bg-amber-100 text-amber-700">{rsvpCounts.maybe} peut-être</Badge>
                <Badge className="bg-red-100 text-red-700">{rsvpCounts.declined} indispo</Badge>
              </div>
              <div className="space-y-1">
                {Object.entries(rsvps).map(([uid, r]) => (
                  <div key={uid} className="flex items-center justify-between text-sm py-1">
                    <span>{r.name}</span>
                    <Badge className={RSVP_BADGE[r.status]?.cls || ""}>{RSVP_BADGE[r.status]?.label || r.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Finish Meal Button (host only) */}
        {isHost && meal.status !== "finished" && (
          <Button
            onClick={handleFinishMeal}
            disabled={finishing}
            className="w-full rounded-full gap-2 bg-green-600 hover:bg-green-700"
            data-testid="finish-meal-btn"
          >
            {finishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Repas terminé — Inviter à noter
          </Button>
        )}
        {meal.status === "finished" && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3 flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" /> Repas terminé
              {meal.recipe_id && !isHost && (
                <Button onClick={handleFavoriteRecipe} disabled={favoriting} variant="outline" size="sm" className="ml-auto rounded-full gap-1.5 border-primary text-primary text-xs" data-testid="favorite-recipe-btn">
                  {favoriting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bookmark className="w-3 h-3" />}
                  Mettre en favoris (+5 pts)
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rating Section (when meal is finished) */}
        {meal.status === "finished" && user && (
          <Card className="border-amber-200" data-testid="rating-section">
            <CardContent className="p-4">
              {hasRated ? (
                <div>
                  <p className="text-sm font-medium text-charcoal mb-3 text-center">Votre note</p>
                  {RATING_CRITERIA.map((c) => (
                    <div key={c.key} className="flex items-center justify-between py-1">
                      <span className="text-sm text-muted-foreground">{c.icon} {c.label}</span>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map((s) => (
                          <Star key={s} className={`w-4 h-4 ${s <= rating[c.key] ? "fill-amber-400 text-amber-400" : "text-gray-200"}`} />
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-center text-sm font-bold text-primary mt-2">
                    {Object.values(rating).reduce((a, b) => a + b, 0)}/20
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-charcoal text-center">
                    Notez ce repas !
                  </p>
                  {RATING_CRITERIA.map((c) => (
                    <div key={c.key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-charcoal">{c.icon} {c.label}</span>
                        <span className="text-xs text-muted-foreground">{rating[c.key]}/5</span>
                      </div>
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map((s) => (
                          <button
                            key={s}
                            onClick={() => setRating((r) => ({ ...r, [c.key]: s }))}
                            onMouseEnter={() => setHoverRating((h) => ({ ...h, [c.key]: s }))}
                            onMouseLeave={() => setHoverRating((h) => ({ ...h, [c.key]: 0 }))}
                            className="p-0.5 transition-transform hover:scale-110"
                            data-testid={`star-${c.key}-${s}`}
                          >
                            <Star className={`w-7 h-7 transition-colors ${
                              s <= (hoverRating[c.key] || rating[c.key])
                                ? "fill-amber-400 text-amber-400"
                                : "text-gray-200"
                            }`} />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="text-center py-1">
                    <span className="text-lg font-bold text-primary">
                      {Object.values(rating).reduce((a, b) => a + b, 0)}/20
                    </span>
                  </div>
                  <Textarea
                    placeholder="Un petit commentaire ? (optionnel)"
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    rows={2}
                    className="text-sm"
                  />
                  <Button
                    onClick={handleSubmitRating}
                    disabled={submittingRating || Object.values(rating).some((v) => v === 0)}
                    className="w-full rounded-full gap-2"
                    data-testid="submit-rating-btn"
                  >
                    {submittingRating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                    Envoyer ma note
                  </Button>
                </div>
              )}

              {/* Average ratings display */}
              {meal.ratings?.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-muted-foreground text-center mb-2">
                    Moyenne ({meal.ratings.length} avis)
                  </p>
                  {RATING_CRITERIA.map((c) => {
                    const avg = (meal.ratings.reduce((a, r) => a + (r[c.key] || 0), 0) / meal.ratings.length).toFixed(1);
                    return (
                      <div key={c.key} className="flex items-center justify-between text-xs py-0.5">
                        <span className="text-muted-foreground">{c.icon} {c.label}</span>
                        <span className="font-medium text-amber-600">{avg}/5</span>
                      </div>
                    );
                  })}
                  <div className="text-center mt-1">
                    <span className="text-sm font-bold text-primary">
                      {(meal.ratings.reduce((a, r) => a + (r.total || 0), 0) / meal.ratings.length).toFixed(1)}/20
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Share Button */}
        <Button onClick={shareLink} variant="outline" className="w-full rounded-full gap-2" data-testid="share-meal-btn">
          <Share2 className="w-4 h-4" /> Partager le repas
        </Button>

        {/* Budget & Contribution Card */}
        {(meal.cost_per_person || meal.estimated_budget || meal.contribution_enabled || isHost) && (
          <Card data-testid="budget-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Wallet className="w-4 h-4" /> Budget & Participation
                {isHost && (
                  <button onClick={() => {
                    setBudgetForm({
                      cost_per_person: meal.cost_per_person || "",
                      estimated_budget: meal.estimated_budget || "",
                      contribution_per_guest: meal.contribution_per_guest || "",
                      contribution_enabled: meal.contribution_enabled || false,
                      budget_hidden: meal.budget_hidden || false,
                    });
                    setEditingBudget(!editingBudget);
                  }} className="ml-auto">
                    <Edit3 className="w-4 h-4 text-muted-foreground hover:text-primary" />
                  </button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-3">
              {/* Host Edit Form */}
              {editingBudget && isHost && (
                <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                  <div className="space-y-1">
                    <label className="text-xs font-medium">Cout par personne</label>
                    <Input type="number" min="0" step="0.50" placeholder="Ex: 12.50"
                      value={budgetForm.cost_per_person}
                      onChange={(e) => setBudgetForm({...budgetForm, cost_per_person: e.target.value})} />
                  </div>
                  <div className="flex items-center gap-2 text-sm cursor-pointer"
                    onClick={() => setBudgetForm({...budgetForm, contribution_enabled: !budgetForm.contribution_enabled})}>
                    <div className={`w-10 h-6 rounded-full transition-colors flex items-center px-0.5 ${budgetForm.contribution_enabled ? "bg-primary justify-end" : "bg-gray-300 justify-start"}`}>
                      <div className="w-5 h-5 rounded-full bg-white shadow-sm" />
                    </div>
                    <span>Demander une participation</span>
                  </div>
                  {budgetForm.contribution_enabled && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium">Montant par personne</label>
                      <Input type="number" min="0" step="0.50" placeholder="Ex: 10"
                        value={budgetForm.contribution_per_guest}
                        onChange={(e) => setBudgetForm({...budgetForm, contribution_per_guest: e.target.value})} />
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm cursor-pointer"
                    onClick={() => setBudgetForm({...budgetForm, budget_hidden: !budgetForm.budget_hidden})}>
                    {budgetForm.budget_hidden
                      ? <EyeOff className="w-5 h-5 text-muted-foreground" />
                      : <Eye className="w-5 h-5 text-muted-foreground" />}
                    <span>{budgetForm.budget_hidden ? "Budget masque aux invites" : "Budget visible par tous"}</span>
                  </div>
                  <Button size="sm" onClick={handleSaveBudget} disabled={savingBudget} className="w-full">
                    {savingBudget ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
                  </Button>
                </div>
              )}

              {/* Budget Display */}
              {!editingBudget && (
                <>
                  {/* If budget is hidden and user is NOT host */}
                  {meal.budget_hidden && !isHost ? (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl text-sm text-muted-foreground">
                      <EyeOff className="w-4 h-4 flex-shrink-0" />
                      <span>L'hote n'a pas partage le detail du budget</span>
                    </div>
                  ) : (
                    <>
                      {meal.cost_per_person && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm text-muted-foreground">Cout par personne</span>
                          <span className="font-semibold text-charcoal">{meal.cost_per_person} EUR</span>
                        </div>
                      )}
                      {meal.estimated_budget && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm text-muted-foreground">Budget total estime</span>
                          <span className="font-semibold text-charcoal">{meal.estimated_budget} EUR</span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Contribution request is always visible if enabled */}
                  {meal.contribution_enabled && meal.contribution_per_guest && (
                    <div className="flex items-center justify-between p-3 bg-primary/5 rounded-xl border border-primary/20">
                      <div>
                        <span className="text-sm font-medium text-charcoal">Participation demandee</span>
                        <p className="text-xs text-muted-foreground">Par personne</p>
                      </div>
                      <span className="text-lg font-bold text-primary">{meal.contribution_per_guest} EUR</span>
                    </div>
                  )}

                  {/* Hidden badge for host */}
                  {meal.budget_hidden && isHost && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <EyeOff className="w-3 h-3" />
                      <span>Le budget est masque pour les invites</span>
                    </div>
                  )}
                </>
              )}

              {/* Budget Summary */}
              {budgetSummary && budgetSummary.per_person?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-charcoal flex items-center gap-1">
                    <ArrowUpDown className="w-3 h-3" /> Participations
                  </p>
                  {budgetSummary.per_person.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 bg-green-50 rounded-lg">
                      <span className="text-charcoal">{p.pseudo}</span>
                      <span className="font-medium text-green-700">{p.paid} EUR</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t text-sm">
                    <span className="text-muted-foreground">Total collecte</span>
                    <span className="font-semibold text-charcoal">{budgetSummary.total_paid} EUR</span>
                  </div>
                  {budgetSummary.remaining > 0 && meal.estimated_budget && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Restant</span>
                      <span className="font-medium text-amber-600">{budgetSummary.remaining} EUR</span>
                    </div>
                  )}
                </div>
              )}

              {/* Contribute Button (for guests) */}
              {user && !isHost && meal.contribution_enabled && (
                <div>
                  {!showContribute ? (
                    <Button onClick={() => { setContribAmount(String(meal.contribution_per_guest || "")); setShowContribute(true); }}
                      variant="outline" className="w-full rounded-full gap-2" data-testid="contribute-btn">
                      <CreditCard className="w-4 h-4" /> J'ai paye ma part
                    </Button>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                      <div className="space-y-1">
                        <label className="text-xs font-medium">Montant</label>
                        <Input type="number" min="0" step="0.50" value={contribAmount}
                          onChange={(e) => setContribAmount(e.target.value)} data-testid="contrib-amount-input" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium">Methode</label>
                        <div className="flex gap-1.5 flex-wrap">
                          {[
                            { val: "cash", label: "Especes", icon: Banknote },
                            { val: "card", label: "Carte", icon: CreditCard },
                            { val: "transfer", label: "Virement", icon: ArrowUpDown },
                          ].map(m => (
                            <button key={m.val} onClick={() => setContribMethod(m.val)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all ${
                                contribMethod === m.val ? "bg-primary text-white border-primary" : "bg-white border-gray-200"
                              }`}>
                              <m.icon className="w-3 h-3" /> {m.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setShowContribute(false)} className="flex-1">Annuler</Button>
                        <Button size="sm" onClick={handleContribute} disabled={submittingContrib} className="flex-1" data-testid="submit-contrib-btn">
                          {submittingContrib ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Recipe Info */}
        {meal.recipe_name && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <GustoIcon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-charcoal">Menu : {meal.recipe_name}</p>
                {meal.recipe_id && (
                  <Link to={`/recipe/${meal.recipe_id}`} className="text-xs text-primary">Voir la recette</Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Participants with RSVP status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Convives ({(meal.participants || []).length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex flex-wrap gap-3">
              {(meal.participants || []).map((p) => {
                const dp = p.dietary_profile || {};
                const pIsHost = p.id === meal.host_id;
                const pRsvp = rsvps[p.id];
                return (
                  <div key={p.id} className="flex items-center gap-2 p-2 rounded-xl bg-gray-50">
                    <ChefAvatar src={p.avatar} name={p.pseudo} size="sm" showToque={false} />
                    <div>
                      <p className="text-sm font-medium">
                        {p.pseudo}
                        {pIsHost && <Badge className="ml-1 text-[10px] bg-primary/10 text-primary">Hôte</Badge>}
                        {pRsvp && !pIsHost && (
                          <Badge className={`ml-1 text-[10px] ${RSVP_BADGE[pRsvp.status]?.cls || ""}`}>
                            {RSVP_BADGE[pRsvp.status]?.label}
                          </Badge>
                        )}
                      </p>
                      {dp.diet_type && dp.diet_type !== "omnivore" && (
                        <p className="text-[10px] text-muted-foreground">{dp.diet_type}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Allergy Warnings */}
        {allAllergies.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-amber-800 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" /> Allergies à prendre en compte
              </p>
              <div className="flex flex-wrap gap-2">
                {allAllergies.map((w, i) => (
                  <Badge key={i} variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">
                    {w.name} : {w.item}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Your Name (for claiming) */}
        <Card>
          <CardContent className="p-4">
            <label className="text-sm font-medium text-charcoal block mb-2">
              Votre prénom (pour réserver des articles)
            </label>
            <Input
              placeholder="Votre prénom"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              data-testid="guest-name-input"
            />
          </CardContent>
        </Card>

        {/* Shopping List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" /> Liste de courses
              <Badge variant="outline">{claimedCount}/{totalItems}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {/* Add item */}
            {token && (
              <div className="mb-3">
                {showAddItem ? (
                  <div className="flex gap-2">
                    <Input placeholder="Ex: 2 baguettes" value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddItem()}
                      data-testid="add-item-input" />
                    <Button size="sm" onClick={handleAddItem} data-testid="add-item-submit">
                      <Plus className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setShowAddItem(false); setNewItemName(""); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setShowAddItem(true)} className="rounded-full gap-1" data-testid="add-item-btn">
                    <Plus className="w-3 h-3" /> Ajouter un article
                  </Button>
                )}
              </div>
            )}
            {totalItems > 0 ? (
              <div className="space-y-2">
                {meal.shopping_list.map((item, i) => {
                  const isClaimed = !!item.claimed_by;
                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${isClaimed ? "bg-green-50" : "bg-gray-50"}`}
                      data-testid={`shopping-item-${item.id}`}
                    >
                      <button
                        onClick={() => isClaimed ? unclaimItem(item.id) : claimItem(item.id)}
                        disabled={claiming === item.id}
                        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isClaimed ? "border-green-500 bg-green-500 text-white" : "border-gray-300 hover:border-primary"
                        }`}
                      >
                        {claiming === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : isClaimed ? <Check className="w-3.5 h-3.5" /> : null}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${isClaimed ? "line-through text-muted-foreground" : "text-charcoal"}`}>
                          {item.quantity && `${item.quantity} `}{item.unit && `${item.unit} `}{item.name}
                        </p>
                        {isClaimed && (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <UserIcon className="w-3 h-3" /> {item.claimed_by_name} s'en charge
                          </p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Pas encore d'articles dans la liste</p>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
          <Heart className="w-3 h-3" /> Propulsé par Gusto
        </p>
      </div>
    </div>
  );
};

export default MealDetailPage;
